import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, IconButton, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack,
  Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, ToggleButtonGroup, ToggleButton, Divider,
  Tabs, Tab, Select, FormControl, InputLabel, Alert,
  Grid, Switch, FormControlLabel,
} from '@mui/material';
import ArrowBackIcon         from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon       from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon      from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FolderOpenIcon        from '@mui/icons-material/FolderOpen';
import PeopleIcon            from '@mui/icons-material/People';
import DeleteIcon            from '@mui/icons-material/Delete';
import PersonAddIcon         from '@mui/icons-material/PersonAdd';
import CreateNewFolderIcon   from '@mui/icons-material/CreateNewFolder';
import useConfirm from '../hooks/useConfirm';

const API_URL = process.env.REACT_APP_API_URL || '';

const MONTHS_SQ = [
  'Janar','Shkurt','Mars','Prill','Maj','Qershor',
  'Korrik','Gusht','Shtator','Tetor','Nëntor','Dhjetor',
];

function getAuthHeaders() {
  // The app stores the session (with token) under `logedInUser` in sessionStorage,
  // with a cookie fallback for "remember me". Read from there — `hr_token` is never set.
  let token = '';
  try {
    const raw = sessionStorage.getItem('logedInUser');
    if (raw) token = JSON.parse(raw)?.token || '';
    if (!token) {
      const cookie = document.cookie.split(';').find(c => c.trim().startsWith('logedInUser='));
      if (cookie) {
        const val = decodeURIComponent(cookie.split('=').slice(1).join('='));
        token = JSON.parse(val)?.token || '';
      }
    }
  } catch { /* noop */ }
  return token ? { Authorization: `Bearer ${token}` } : {};
}
function isAdminUser() {
  try {
    let raw = sessionStorage.getItem('logedInUser') || localStorage.getItem('logedInUser') || '';
    if (!raw) {
      const cookie = document.cookie.split(';').find(c => c.trim().startsWith('logedInUser='));
      if (cookie) raw = decodeURIComponent(cookie.split('=').slice(1).join('='));
    }
    if (!raw) return false;
    const u = JSON.parse(raw);
    const roles = (u?.roles || u?.data?.roles || []).map(r => String(r).toLowerCase());
    // Must match the backend role gate on the project write endpoints
    // (POST/PUT /api/projects, /work-rules). 'manager'/'hr' are intentionally
    // excluded — the API returns 403 for them, so showing the UI would mislead.
    return roles.some(r => ['admin', 'superadmin', 'menaxher'].includes(r));
  } catch { return false; }
}
const localStr = (d) => {
  const n = new Date(d);
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
};
const fmtHM = (min) => {
  if (!min && min !== 0) return '—';
  const h = Math.floor(Math.abs(min) / 60);
  const m = Math.abs(min) % 60;
  return `${min < 0 ? '-' : ''}${h}h ${String(m).padStart(2,'0')}m`;
};

const TODAY_STR = localStr(new Date());

// ─── Color palette ────────────────────────────────────────────────────────────
const C = {
  work: '#e8f5e9',   workFg: '#2e7d32',
  late: '#fff3e0',   lateFg: '#e65100',
  absent: '#ffebee', absFg: '#c62828',
  off: '#f5f5f5',    offFg: '#bbb',
  today: '#1565c0',
  border: '#e2e8f0',
  borderWk: '#b0bec5',
  head: '#f8fafc',
};

const NAME_W = 160;
const ROW_H  = 34;
const CELL_W = 30;

const cellSt = (extra = {}) => ({
  padding: '0 1px',
  textAlign: 'center',
  fontSize: 9,
  border: `1px solid ${C.border}`,
  minWidth: CELL_W,
  width: CELL_W,
  height: ROW_H,
  boxSizing: 'border-box',
  ...extra,
});

// ─── Workers Matrix ───────────────────────────────────────────────────────────
function WorkersMatrix({ data, days, weeks, onDayClick, onWorkerClick }) {
  if (!data?.length) {
    return (
      <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
        <PeopleIcon sx={{ fontSize: 36, color: '#cfd8dc', mb: 1 }} />
        <Typography variant="body2">Nuk ka punëtorë të caktuar për këtë projekt</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            {/* Name sticky header */}
            <th style={{
              position: 'sticky', left: 0, zIndex: 4,
              background: C.head, border: `1px solid ${C.border}`,
              width: NAME_W, minWidth: NAME_W,
              fontSize: 10, padding: '4px 8px', fontWeight: 700, textAlign: 'left',
            }}>
              Punëtori
            </th>

            {/* Day headers per week */}
            {weeks.map(wk => (
              <React.Fragment key={wk.iso_week}>
                {wk.dates.map(date => {
                  const d = days.find(x => x.date === date);
                  const isToday = date === TODAY_STR;
                  const isWE    = d?.is_weekend;
                  return (
                    <th key={date} style={cellSt({
                      background: isToday ? '#dde8f8' : isWE ? '#f0f0f0' : C.head,
                      fontWeight: isToday ? 900 : 600,
                      color: isToday ? C.today : isWE ? '#888' : '#64748b',
                      borderLeft:  isToday ? `2px solid ${C.today}` : undefined,
                      borderRight: isToday ? `2px solid ${C.today}` : undefined,
                      verticalAlign: 'middle',
                      lineHeight: 1.2,
                      padding: '2px 1px',
                    })}>
                      <div style={{ fontSize: 8, color: isToday ? C.today : '#94a3b8' }}>{d?.day_short}</div>
                      <div style={{ fontSize: 9 }}>{d?.day_num}</div>
                    </th>
                  );
                })}
                {/* KW header */}
                <th style={cellSt({
                  background: '#f0f4ff', color: '#4c5bcc',
                  fontWeight: 700, fontSize: 8,
                  borderLeft: `2px solid ${C.borderWk}`,
                  borderRight: `2px solid ${C.borderWk}`,
                })}>
                  KW{wk.iso_week}
                </th>
              </React.Fragment>
            ))}

            {/* Monthly total header */}
            <th style={cellSt({
              background: '#eef2ff', color: '#3730a3',
              fontWeight: 700, fontSize: 9,
              borderLeft: '2px solid #818cf8',
            })}>
              Total
            </th>
          </tr>
        </thead>

        <tbody>
          {data.map(w => {
            const diff = Number(w.diff_minutes || 0);
            return (
              <tr
                key={w.worker_id}
                style={{ height: ROW_H }}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.95)'}
                onMouseLeave={e => e.currentTarget.style.filter = ''}
              >
                {/* Name cell — sticky, clickable */}
                <td
                  onClick={() => onWorkerClick && onWorkerClick(w)}
                  style={{
                    position: 'sticky', left: 0, zIndex: 2,
                    background: '#f8fafc', border: `1px solid ${C.border}`,
                    padding: '0 8px', fontSize: 11, fontWeight: 600, color: '#1a2b47',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    maxWidth: NAME_W, cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 9, color: '#90a4ae', marginRight: 4 }}>
                    {w.schedule_start ? String(w.schedule_start).slice(0,5) : ''}
                  </span>
                  <span style={{ borderBottom: '1px dashed #90caf9' }}>
                    {`${w.first_name || ''} ${w.last_name || ''}`.trim()}
                  </span>
                </td>

                {/* Day cells */}
                {weeks.map(wk => (
                  <React.Fragment key={wk.iso_week}>
                    {wk.dates.map(date => {
                      const day = w.days?.[date];
                      const isToday = date === TODAY_STR;

                      if (!day) return <td key={date} style={cellSt({ background: '#fafafa' })} />;

                      let bg, fg;
                      if (!day.is_work_day)          { bg = C.off;    fg = C.offFg; }
                      else if (day.status === 'absent') { bg = C.absent; fg = C.absFg; }
                      else if (day.late_minutes > 0) { bg = C.late;   fg = C.lateFg; }
                      else                           { bg = C.work;   fg = C.workFg; }

                      const ciStr = day.check_in  ? String(day.check_in).slice(0,5)  : null;
                      const coStr = day.check_out ? String(day.check_out).slice(0,5) : null;

                      return (
                        <td
                          key={date}
                          onClick={(e) => { e.stopPropagation(); if (onDayClick) onDayClick(w, date, day); }}
                          style={cellSt({
                            background: bg, color: fg,
                            fontWeight: day.status === 'absent' ? 800 : 500,
                            cursor: 'pointer',
                            borderLeft:  isToday ? `2px solid ${C.today}` : undefined,
                            borderRight: isToday ? `2px solid ${C.today}` : undefined,
                            position: 'relative',
                            verticalAlign: 'middle',
                            lineHeight: 1.25,
                          })}
                        >
                          {day.late_minutes > 0 && (
                            <span style={{
                              position: 'absolute', top: 0, right: 0,
                              width: 0, height: 0, borderStyle: 'solid',
                              borderWidth: '0 6px 6px 0',
                              borderColor: `transparent ${C.lateFg} transparent transparent`,
                            }} />
                          )}
                          {day.status === 'absent' ? (
                            <span style={{ fontSize: 10, fontWeight: 800 }}>A</span>
                          ) : day.is_work_day && ciStr ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <span style={{ fontSize: 8, color: '#2e6e44', fontWeight: 600 }}>{ciStr}</span>
                              <span style={{ fontSize: 8, color: '#7a3030', fontWeight: 600 }}>{coStr || '--:--'}</span>
                            </div>
                          ) : null}
                        </td>
                      );
                    })}

                    {/* Weekly total */}
                    {(() => {
                      const wt  = w.weekly_totals?.[wk.iso_week] || { worked: 0, planned: 0 };
                      const wd  = wt.worked - wt.planned;
                      const wBg = wt.planned === 0 ? '#f5f5f5' : wd >= 0 ? '#f3e5f5' : '#ffebee';
                      const wFg = wt.planned === 0 ? '#bbb' : wd >= 0 ? '#6a1b9a' : '#c62828';
                      return (
                        <td style={cellSt({
                          background: wBg, color: wFg,
                          fontWeight: 700, fontSize: 8,
                          borderLeft:  `2px solid ${C.borderWk}`,
                          borderRight: `2px solid ${C.borderWk}`,
                        })}>
                          {fmtHM(wt.worked)}
                        </td>
                      );
                    })()}
                  </React.Fragment>
                ))}

                {/* Monthly total */}
                <td style={cellSt({
                  background: diff >= 0 ? '#e3f2fd' : '#ffebee',
                  color: diff >= 0 ? '#1565c0' : '#c62828',
                  fontWeight: 700, fontSize: 8,
                  borderLeft: '2px solid #5c6bc0',
                })}>
                  {fmtHM(w.total_worked)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
}

// ─── Day Detail Dialog ────────────────────────────────────────────────────────
function DayDetailDialog({ open, info, onClose, onSaved, isAdmin }) {
  const [adjMin,  setAdjMin]  = useState('');
  const [reason,  setReason]  = useState('');
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');

  useEffect(() => {
    if (open) { setAdjMin(''); setReason(''); setErr(''); }
  }, [open]);

  if (!info) return null;
  const { worker, date, day } = info;
  const workerName  = `${worker.first_name || ''} ${worker.last_name || ''}`.trim();
  const isAbsent    = day.status === 'absent';
  const isDayOff    = !day.is_work_day && !day.check_in; // pushim pa punë
  const isLate      = (day.late_minutes || 0) > 0;
  const statusColor = isAbsent ? '#c62828' : isDayOff ? '#90a4ae' : isLate ? '#e65100' : '#2e7d32';
  const statusLabel = isAbsent ? 'Mungon' : isDayOff ? 'Pushim' : isLate ? `Vonë ${day.late_minutes} min` : 'Prezent';
  const workedStr   = fmtHM(day.net_worked_minutes);
  const ciStr       = day.check_in  ? String(day.check_in).slice(0,5)  : null;
  const coStr       = day.check_out ? String(day.check_out).slice(0,5) : null;

  const quickAdd = (delta) => setAdjMin(v => String((parseInt(v) || 0) + delta));

  const handleSave = async () => {
    const delta = parseInt(adjMin, 10);
    if (!adjMin || isNaN(delta)) { setErr('Shkruaj minutat (p.sh. 15 ose -30)'); return; }
    if (!reason.trim())          { setErr('Arsyeja është e detyrueshme'); return; }

    setSaving(true); setErr('');
    try {
      const currentMin = day.net_worked_minutes || 0;
      const newWorkMin = Math.max(0, currentMin + delta);

      const resp = await fetch(`${API_URL}/api/attendance/adjustments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          worker_id:       worker.worker_id,
          adj_date:        date,
          adj_work_minutes: newWorkMin,
          reason:          reason.trim(),
        }),
      });
      const json = await resp.json();
      if (json.success) {
        onSaved?.();
        onClose();
      } else {
        setErr(json.error || 'Gabim gjatë ruajtjes');
      }
    } catch {
      setErr('Gabim i rrjetit — kontrollo lidhjen');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5, fontWeight: 700 }}>
        {workerName}
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.25 }}>
          {date}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack gap={1.5}>
          {/* Status badge */}
          <Box>
            <Box sx={{
              display: 'inline-flex', px: 1.5, py: 0.4, borderRadius: 2,
              background: `${statusColor}18`, color: statusColor, fontWeight: 700, fontSize: 13,
            }}>
              {statusLabel}
            </Box>
          </Box>

          {/* Check-in/out + worked hours */}
          {!isAbsent && !isDayOff && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, background: '#f0f7f2', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">Hyrja</Typography>
                <Typography fontWeight={700} fontSize={18} color="#2e6e44">{ciStr || '—'}</Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, background: '#fdf3f3', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">Dalja</Typography>
                <Typography fontWeight={700} fontSize={18} color="#7a3030">{coStr || '—'}</Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, background: '#f4f6fb', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">Orë punuar</Typography>
                <Typography fontWeight={700} fontSize={16}>{workedStr}</Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, background: '#f4f6fb', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">Vonesa</Typography>
                <Typography fontWeight={700} fontSize={16} color={isLate ? '#e65100' : 'text.primary'}>
                  {isLate ? `${day.late_minutes} min` : '—'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Admin: add / remove minutes */}
          {isAdmin && !isAbsent && !isDayOff && (
            <>
              <Divider />
              <Typography variant="caption" color="text.secondary" fontWeight={700}
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Korrigjim minutash
              </Typography>

              {/* Quick buttons */}
              <Stack direction="row" gap={0.75} flexWrap="wrap">
                <Button size="small" variant="outlined" color="success" sx={{ fontSize: 11 }} onClick={() => quickAdd(15)}>+15 min</Button>
                <Button size="small" variant="outlined" color="success" sx={{ fontSize: 11 }} onClick={() => quickAdd(30)}>+30 min</Button>
                <Button size="small" variant="outlined" color="success" sx={{ fontSize: 11 }} onClick={() => quickAdd(60)}>+1 orë</Button>
                <Button size="small" variant="outlined" color="error"   sx={{ fontSize: 11 }} onClick={() => quickAdd(-15)}>-15 min</Button>
                <Button size="small" variant="outlined" color="error"   sx={{ fontSize: 11 }} onClick={() => quickAdd(-30)}>-30 min</Button>
              </Stack>

              {/* Manual input */}
              <TextField
                label="Minuta (+ shto / - hiq)"
                type="number"
                size="small"
                value={adjMin}
                onChange={e => setAdjMin(e.target.value)}
                inputProps={{ step: 5 }}
                helperText={adjMin ? `Orë të reja: ${fmtHM((day.net_worked_minutes || 0) + (parseInt(adjMin) || 0))}` : ' '}
              />

              <TextField
                label="Arsyeja *"
                size="small"
                multiline
                rows={2}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="p.sh. Korrigjim manual — karta u shënua me vonesë"
              />

              {err && <Typography color="error" variant="caption">{err}</Typography>}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">Mbyll</Button>
        {isAdmin && !isAbsent && !isDayOff && (
          <Button onClick={handleSave} variant="contained" size="small" disabled={saving || !adjMin}>
            {saving ? 'Duke ruajtur...' : 'Ruaj korrigjimin'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── ProjectWorkersTab ────────────────────────────────────────────────────────
function ProjectWorkersTab({ project, allWorkers }) {
  const projectId = project.id || project.Id;
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [form, setForm]               = useState({ worker_id: '', start_date: '', end_date: '' });
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/projects/${projectId}/assignments`, { headers: getAuthHeaders() });
      const d = await r.json();
      setAssignments(d.data || []);
    } catch {}
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.worker_id || !form.start_date) return setErr('Punëtori dhe data e fillimit janë të detyrueshme');
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/workers/${form.worker_id}/project-assignments`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: Number(projectId), start_date: form.start_date, end_date: form.end_date || null }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) setErr(d.message || d.error || 'Gabim gjatë ruajtjes');
      else { setForm({ worker_id: '', start_date: '', end_date: '' }); load(); }
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const [confirm, confirmUI] = useConfirm();


  const handleDelete = async (workerId, assignId) => {
    if (!(await confirm({ title: 'Fshi caktimin', message: 'Fshi këtë caktim?', confirmLabel: 'Fshi' }))) return;
    await fetch(`${API_URL}/api/workers/${workerId}/project-assignments/${assignId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    load();
  };

  const isActive = (a) => a.start_date <= TODAY_STR && (!a.end_date || a.end_date >= TODAY_STR);

  // workers not yet active in this project (for dropdown)
  const activeWorkerIds = new Set(assignments.filter(isActive).map(a => String(a.worker_id)));

  return (
    <Box>
      {/* Add form */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2, border: '1px solid #e8f0fe' }}>
        <Typography variant="body2" fontWeight={700} mb={1.5} color="#1a237e">
          <PersonAddIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
          Cakto punëtor të ri në projekt
        </Typography>
        {err && <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>{err}</Alert>}
        <Grid container spacing={1.5} alignItems="flex-end">
          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.82rem' }}>Punëtori</InputLabel>
              <Select
                value={form.worker_id} label="Punëtori"
                onChange={e => { setForm(f => ({ ...f, worker_id: e.target.value })); setErr(''); }}
                sx={{ fontSize: '0.82rem' }}
              >
                <MenuItem value=""><em>— Zgjidhni punëtorin —</em></MenuItem>
                {allWorkers.map(w => {
                  const wid = String(w.Id || w.id);
                  return (
                    <MenuItem key={wid} value={wid} sx={{ fontSize: '0.82rem' }}>
                      {w.FullName || `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim()}
                      {activeWorkerIds.has(wid) && (
                        <Chip label="aktiv" size="small" sx={{ ml: 1, fontSize: 10, height: 18, bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                      )}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              fullWidth size="small" type="date" label="Data fillimit"
              InputLabelProps={{ shrink: true }}
              value={form.start_date}
              onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
              sx={{ '& input': { fontSize: '0.82rem' } }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              fullWidth size="small" type="date" label="Data mbarimit (opsionale)"
              InputLabelProps={{ shrink: true }}
              value={form.end_date}
              onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
              inputProps={{ min: form.start_date || undefined }}
              sx={{ '& input': { fontSize: '0.82rem' } }}
            />
          </Grid>
          <Grid item xs={12} sm={2} md={3}>
            <Button
              fullWidth variant="contained" size="small"
              startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <PersonAddIcon />}
              onClick={handleAdd} disabled={saving}
              sx={{ textTransform: 'none', height: 38, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
            >
              {saving ? 'Duke ruajtur...' : 'Cakto në projekt'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Assignments table */}
      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : assignments.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
          <PeopleIcon sx={{ fontSize: 40, color: '#cfd8dc', mb: 1 }} />
          <Typography variant="body2">Nuk ka punëtorë të caktuar për këtë projekt</Typography>
          <Typography variant="caption" color="text.disabled">Shto punëtor nga forma lart</Typography>
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', background: '#f8fafc' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {assignments.length} caktime gjithsej · {assignments.filter(isActive).length} aktive
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Punëtori</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Data fillimit</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Data mbarimit</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Statusi</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map(a => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{a.worker_name}</Typography>
                    {a.position && <Typography variant="caption" color="text.secondary" display="block">{a.position}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{a.start_date}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={!a.end_date ? 'text.disabled' : 'text.primary'}>
                      {a.end_date || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {isActive(a)
                      ? <Chip label="Aktiv" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 11 }} />
                      : <Chip label="Përfunduar" size="small" sx={{ bgcolor: '#f5f5f5', color: '#9e9e9e', fontSize: 11 }} />
                    }
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => handleDelete(a.worker_id, a.id)}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      {confirmUI}
    </Box>
  );
}

// ─── Main FTEPlanPage ─────────────────────────────────────────────────────────
export default function FTEPlanPage() {
  const history = useHistory();
  const { projectId: urlProjectId } = useParams();

  /**
   * Klikimi mbi një punëtor hap «Orarin Individual Mujor» — pamjen vetëm-shikim.
   *
   * Këtu hapej rrjeta Stundenzettel; ajo u hoq nga moduli, dhe klikimi nuk
   * guxonte të mbetej pa kuptim. I njëjti destinacion si nga Plani i Punës, që
   * orari i një personi të lexohet në një vend të vetëm.
   */
  const openIndividualSchedule = (w) => {
    const id = w?.worker_id ?? w?.Id ?? w?.id;
    if (id) history.push(`/home/reports/work-schedule-matrix?worker=${id}`);
  };

  const now   = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [view,            setView]            = useState('list');     // 'list' | 'detail'
  const [selectedProject, setSelectedProject] = useState(null);

  const [projects, setProjects] = useState([]);
  const [workers,  setWorkers]  = useState([]); // all workers (for ProjectWorkersTab)
  const [fteCounts, setFteCounts] = useState({}); // { [projectId]: count }

  const [summaryData,  setSummaryData]  = useState([]);
  const [summaryDays,  setSummaryDays]  = useState([]);
  const [summaryWeeks, setSummaryWeeks] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  // Set when the context-menu "Export" opens a project — triggers CSV export
  // once its schedule data has finished loading.
  const [pendingExport, setPendingExport] = useState(false);

  // Per-project hour-rules editor (opened from the row ⌄ menu)
  const [wrDlg, setWrDlg]         = useState(null);   // project being edited
  const [wrForm, setWrForm]       = useState(null);   // rule values (minutes)
  const [wrLoading, setWrLoading] = useState(false);
  const [wrSaving, setWrSaving]   = useState(false);
  const [wrErr, setWrErr]         = useState('');

  const openWorkRules = async (p) => {
    const pid = p.id || p.Id;
    setWrDlg(p); setWrForm(null); setWrErr(''); setWrLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/projects/${pid}/work-rules`, { headers: getAuthHeaders() }).then(x => x.json());
      if (r?.success) setWrForm(r.data);
      else { setWrErr(r?.error || 'Gabim gjatë ngarkimit'); }
    } catch (e) { setWrErr(e.message); }
    finally { setWrLoading(false); }
  };

  // Paid hours/day = gross − unpaid break (the rule the client described).
  const wrPaidMinutes = wrForm
    ? Math.max(0, (wrForm.daily_gross_minutes || 0) - (wrForm.break_paid ? 0 : (wrForm.break_minutes || 0)))
    : 0;

  const saveWorkRules = async () => {
    if (!wrDlg || !wrForm) return;
    const pid = wrDlg.id || wrDlg.Id;
    setWrSaving(true); setWrErr('');
    try {
      const payload = { ...wrForm, paid_daily_minutes: wrPaidMinutes };
      const r = await fetch(`${API_URL}/api/projects/${pid}/work-rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      }).then(x => x.json());
      if (r?.success) setWrDlg(null);
      else setWrErr(r?.error || 'Ruajtja dështoi');
    } catch (e) { setWrErr(e.message); }
    finally { setWrSaving(false); }
  };

  // Period mode for detail view
  const [periodMode, setPeriodMode] = useState('monthly');
  const [focusDate,  setFocusDate]  = useState(localStr(new Date()));

  // Keep focusDate inside the currently loaded month, so the month arrows also
  // drive the Ditor/Javor views. Without this, navigating months in those modes
  // leaves focusDate in the old month and the matrix renders empty.
  useEffect(() => {
    const first = `${year}-${String(month).padStart(2,'0')}-01`;
    const dim   = new Date(year, month, 0).getDate();
    const last  = `${year}-${String(month).padStart(2,'0')}-${String(dim).padStart(2,'0')}`;
    setFocusDate(fd =>
      (fd >= first && fd <= last) ? fd
        : (TODAY_STR >= first && TODAY_STR <= last) ? TODAY_STR
        : first
    );
  }, [year, month]);

  // Context menu
  const [contextMenu, setContextMenu] = useState(null); // { anchorEl, project }
  // Day detail dialog
  const [dayDetailDlg, setDayDetailDlg] = useState(null);
  // Detail tabs: 0=Orari, 1=Punëtorët (parazgjedhja — rrjeta s'hapet më vetë)
  const [detailTab, setDetailTab] = useState(1);

  // New-project dialog
  const [newDlg, setNewDlg]     = useState(false);
  const [newForm, setNewForm]   = useState({ name: '', description: '', start_date: '', end_date: '', status: 'active', lead_id: '' });
  const [creating, setCreating] = useState(false);
  const [newErr, setNewErr]     = useState('');

  const isAdmin = useMemo(() => isAdminUser(), []);

  const handleCreateProject = async () => {
    if (!newForm.name.trim()) { setNewErr('Emri i projektit është i detyrueshëm'); return; }
    setCreating(true); setNewErr('');
    try {
      const r = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newForm.name.trim(),
          description: newForm.description.trim() || null,
          start_date: newForm.start_date || null,
          end_date: newForm.end_date || null,
          status: newForm.status || 'active',
          lead_id: newForm.lead_id || null,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) { setNewErr(d.error || d.message || 'Gabim gjatë ruajtjes'); }
      else {
        setNewDlg(false);
        setNewForm({ name: '', description: '', start_date: '', end_date: '', status: 'active', lead_id: '' });
        await loadProjects();
      }
    } catch (e) { setNewErr(e.message || 'Gabim i rrjetit'); }
    setCreating(false);
  };

  // ── Load projects + workers ────────────────────────────────────────────────
  const loadProjects = useCallback(() => {
    return fetch(`${API_URL}/api/projects`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(j => setProjects(j.data || []))
      .catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    const headers = getAuthHeaders();
    Promise.all([
      fetch(`${API_URL}/api/projects`, { headers })
        .then(r => r.json()).catch(() => ({ data: [] })),
      fetch(`${API_URL}/api/query/selectworker`, { headers })
        .then(r => r.json()).catch(() => ({ data: [] })),
      fetch(`${API_URL}/api/projects/fte-counts`, { headers })
        .then(r => r.json()).catch(() => ({ data: {} })),
    ]).then(([pRes, wRes, fteRes]) => {
      setProjects(pRes.data || []);
      setWorkers(wRes.data || []);
      setFteCounts(fteRes.data || {});
    });
  }, []);

  const refreshFteCounts = useCallback(() => {
    fetch(`${API_URL}/api/projects/fte-counts`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(j => setFteCounts(j.data || {}))
      .catch(() => {});
  }, []);

  // ── Load detail matrix ─────────────────────────────────────────────────────
  const loadDetail = useCallback(async (projectId, y, m) => {
    setDetailLoading(true);
    try {
      const url = `${API_URL}/api/reports/work-schedule-overview?year=${y}&month=${m}&project_id=${projectId}`;
      const resp = await fetch(url, { headers: getAuthHeaders() });
      const json = await resp.json();
      if (json.success) {
        setSummaryData(json.workers || []);
        setSummaryDays(json.days    || []);
        setSummaryWeeks(json.weeks  || []);
      }
    } catch (e) {
      console.error('[FTEPlan loadDetail]', e.message);
    }
    setDetailLoading(false);
  }, []);

  // Reload when month/year changes while in detail view
  useEffect(() => {
    if (view === 'detail' && selectedProject) {
      loadDetail(selectedProject.id || selectedProject.Id, year, month);
    }
  }, [year, month, view, selectedProject, loadDetail]);

  // ── Month navigation ───────────────────────────────────────────────────────
  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  // ── Open detail ────────────────────────────────────────────────────────────
  /**
   * Klikimi mbi projekt hap «Orët» e atij projekti (kërkesë e klientit), jo
   * rrjetën mujore me «A». Rrjeta mbetet te «Fte Plan» → skeda «Orari».
   */
  const openHours = useCallback((project) => {
    const pid = project.id || project.Id;
    history.push(`/oret?project=${pid}`);
  }, [history]);

  const openDetail = useCallback((project, tab = 1) => {
    const pid = project.id || project.Id;
    setSelectedProject(project);
    setPeriodMode('monthly');
    // «Fte Plan» hapet te «Punëtorët / Cakto»; «Orari» (rrjeta) është skeda e dytë.
    setDetailTab(tab);
    setView('detail');
    loadDetail(pid, year, month);
    history.push(`/fte-plan/${pid}`);
  }, [year, month, loadDetail, history]);

  // Auto-open project from URL on initial load
  useEffect(() => {
    if (!urlProjectId || projects.length === 0) return;
    if (view === 'detail') return; // already open
    const match = projects.find(p => String(p.id || p.Id) === String(urlProjectId));
    if (match) {
      setSelectedProject(match);
      setPeriodMode('monthly');
      setView('detail');
      loadDetail(urlProjectId, year, month);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProjectId, projects]);

  // ── Period filter ──────────────────────────────────────────────────────────
  const weekBounds = useMemo(() => {
    const d = new Date(focusDate); d.setHours(0,0,0,0);
    const dow = (d.getDay() + 6) % 7; // Monday=0
    const mon = new Date(d); mon.setDate(d.getDate() - dow);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { start: localStr(mon), end: localStr(sun) };
  }, [focusDate]);

  const displayDays = useMemo(() => {
    if (periodMode === 'monthly') return summaryDays;
    if (periodMode === 'daily')   return summaryDays.filter(d => d.date === focusDate);
    return summaryDays.filter(d => d.date >= weekBounds.start && d.date <= weekBounds.end);
  }, [summaryDays, periodMode, focusDate, weekBounds]);

  const displayWeeks = useMemo(() => {
    if (periodMode === 'monthly') return summaryWeeks;
    const ds = new Set(displayDays.map(d => d.date));
    return summaryWeeks
      .map(wk => ({ ...wk, dates: wk.dates.filter(d => ds.has(d)) }))
      .filter(wk => wk.dates.length > 0);
  }, [summaryWeeks, displayDays, periodMode]);

  const periodLabel = useMemo(() => {
    if (periodMode === 'monthly') return `${MONTHS_SQ[month-1]} ${year}`;
    if (periodMode === 'daily') {
      const d = new Date(focusDate);
      return `${d.getDate()} ${MONTHS_SQ[d.getMonth()]} ${d.getFullYear()}`;
    }
    const ds = new Date(weekBounds.start), de = new Date(weekBounds.end);
    return `${ds.getDate()}.${String(ds.getMonth()+1).padStart(2,'0')} – ${de.getDate()}.${String(de.getMonth()+1).padStart(2,'0')}.${de.getFullYear()}`;
  }, [periodMode, focusDate, month, year, weekBounds]);

  const shiftFocus = (days) => {
    const d = new Date(focusDate);
    d.setDate(d.getDate() + days);
    setFocusDate(localStr(d));
  };

  // ── FTE count map per project — from worker_project_assignments (active today)
  const fteMap = useMemo(() => fteCounts, [fteCounts]);

  // ── Export current view to CSV ─────────────────────────────────────────────
  const exportToCsv = useCallback(() => {
    if (!summaryData.length || !displayDays.length) return;
    const projectName = selectedProject?.name || selectedProject?.Name || 'export';
    const dateHeader = displayDays.map(d => d.date);
    const header = ['Punëtori', 'Ora fillimit', ...dateHeader, 'Total (min)'];
    const rows = summaryData.map(w => {
      const name = `${w.first_name || ''} ${w.last_name || ''}`.trim();
      const schedStart = w.schedule_start ? String(w.schedule_start).slice(0, 5) : '';
      const dayCells = dateHeader.map(date => {
        const d = w.days?.[date];
        if (!d) return '';
        if (d.status === 'absent') return 'A';
        if (!d.is_work_day) return 'OFF';
        if (d.check_in) {
          const ci = String(d.check_in).slice(0, 5);
          const co = d.check_out ? String(d.check_out).slice(0, 5) : '--:--';
          return `${ci}-${co}`;
        }
        return '';
      });
      return [name, schedStart, ...dayCells, w.total_worked || 0];
    });
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FTE_${projectName.replace(/\s+/g, '_')}_${MONTHS_SQ[month - 1]}_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [summaryData, displayDays, selectedProject, month, year]);

  // Run a queued export (from the context menu) once detail data is ready.
  useEffect(() => {
    if (pendingExport && !detailLoading && summaryData.length && displayDays.length) {
      exportToCsv();
      setPendingExport(false);
    }
  }, [pendingExport, detailLoading, summaryData, displayDays, exportToCsv]);

  // ── Detail view ────────────────────────────────────────────────────────────
  if (view === 'detail' && selectedProject) {
    const projectName = selectedProject.name || selectedProject.Name || '';

    return (
      <Box>
        {/* Detail header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Button
            startIcon={<ArrowBackIcon />} size="small" variant="outlined"
            onClick={() => { setView('list'); setSelectedProject(null); history.push('/fte-plan'); refreshFteCounts(); }}
            sx={{ textTransform: 'none' }}
          >
            Kthehu
          </Button>

          <Typography variant="h6" fontWeight={700} sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {projectName}
          </Typography>

          {/* Month navigation — only for schedule tab */}
          {detailTab === 0 && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <IconButton size="small" onClick={prevMonth}><ChevronLeftIcon /></IconButton>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 130, textAlign: 'center' }}>
                {periodLabel}
              </Typography>
              <IconButton size="small" onClick={nextMonth}><ChevronRightIcon /></IconButton>
            </Stack>
          )}

          {/* Period toggle — only for schedule tab */}
          {detailTab === 0 && (
            <ToggleButtonGroup
              value={periodMode} exclusive size="small"
              onChange={(_, v) => v && setPeriodMode(v)}
              sx={{ '& .MuiToggleButton-root': { fontSize: 11, py: 0.5, px: 1.5 } }}
            >
              <ToggleButton value="daily">Ditor</ToggleButton>
              <ToggleButton value="weekly">Javor</ToggleButton>
              <ToggleButton value="monthly">Mujor</ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Day/week sub-navigation */}
          {detailTab === 0 && periodMode !== 'monthly' && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <IconButton size="small" onClick={() => shiftFocus(periodMode === 'daily' ? -1 : -7)}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" sx={{ minWidth: 96, textAlign: 'center', fontWeight: 600 }}>
                {periodMode === 'daily' ? focusDate : `${weekBounds.start} → ${weekBounds.end}`}
              </Typography>
              <IconButton size="small" onClick={() => shiftFocus(periodMode === 'daily' ? 1 : 7)}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}

          {/* Export button — only on schedule tab */}
          {detailTab === 0 && summaryData.length > 0 && (
            <Button
              size="small" variant="outlined"
              onClick={exportToCsv}
              sx={{ textTransform: 'none', fontSize: 11, py: 0.5, borderColor: '#4caf50', color: '#2e7d32',
                '&:hover': { borderColor: '#2e7d32', background: '#f1f8f1' } }}
            >
              Export CSV
            </Button>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={detailTab}
            onChange={(_, v) => setDetailTab(v)}
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 40 } }}
          >
            <Tab label="Orari" value={0} />
            <Tab
              label={
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <PersonAddIcon sx={{ fontSize: 15 }} />
                  Punëtorët / Cakto
                </Stack>
              }
              value={1}
            />
          </Tabs>
        </Box>

        {/* Tab 0: Schedule Matrix */}
        {detailTab === 0 && (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {detailLoading ? (
              <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress size={30} /></Box>
            ) : (
              <WorkersMatrix
                data={summaryData}
                days={displayDays}
                weeks={displayWeeks}
                onDayClick={(worker, date, day) => setDayDetailDlg({ worker, date, day })}
                onWorkerClick={(w) => openIndividualSchedule(w)}
              />
            )}
          </Paper>
        )}

        {/* Tab 1: Workers / Assign */}
        {detailTab === 1 && (
          <ProjectWorkersTab project={selectedProject} allWorkers={workers} />
        )}

        {/* Day detail dialog */}
        <DayDetailDialog
          open={!!dayDetailDlg}
          info={dayDetailDlg}
          onClose={() => setDayDetailDlg(null)}
          onSaved={() => loadDetail(selectedProject.id || selectedProject.Id, year, month)}
          isAdmin={isAdmin}
        />

      </Box>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
        <FolderOpenIcon sx={{ color: '#5c6bc0', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700}>Plani</Typography>
        <Chip
          label={`${projects.length} projekte`} size="small"
          sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 700 }}
        />
        <Box sx={{ flex: 1 }} />

        {/* Month filter */}
        <Stack direction="row" alignItems="center" gap={0.5}>
          <IconButton size="small" onClick={prevMonth}><ChevronLeftIcon /></IconButton>
          <Typography variant="body2" fontWeight={700} sx={{ minWidth: 110, textAlign: 'center' }}>
            {MONTHS_SQ[month-1]} {year}
          </Typography>
          <IconButton size="small" onClick={nextMonth}><ChevronRightIcon /></IconButton>
        </Stack>

        {/* New project */}
        {isAdmin && (
          <Button
            variant="contained" size="small" startIcon={<CreateNewFolderIcon />}
            onClick={() => { setNewErr(''); setNewDlg(true); }}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, whiteSpace: 'nowrap' }}
          >
            Projekt i ri
          </Button>
        )}
      </Box>

      {/* Projects table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', background: '#f8fafc' }}>
          <Typography variant="body2" color="text.secondary">
            Lista e projekteve — {MONTHS_SQ[month-1]} {year}
            {projects.length > 0 && (
              <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                (Shfaqur {projects.length} prej {projects.length} rreshtave)
              </Typography>
            )}
          </Typography>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Projekti</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, display: { xs: 'none', sm: 'table-cell' } }}>Klienti</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Statusi</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, textAlign: 'center' }}>
                <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
                  <PeopleIcon sx={{ fontSize: 14 }} />
                  Punëtorë
                </Stack>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Veprime</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <FolderOpenIcon sx={{ fontSize: 40, color: '#cfd8dc', mb: 1, display: 'block', mx: 'auto' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Nuk ka ende projekte</Typography>
                  {isAdmin && (
                    <>
                      <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 1.5 }}>
                        Krijo projektin e parë për të filluar planifikimin e FTE-ve
                      </Typography>
                      <Button
                        variant="contained" size="small" startIcon={<CreateNewFolderIcon />}
                        onClick={() => { setNewErr(''); setNewDlg(true); }}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
                      >
                        Projekt i ri
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ) : projects.map(p => {
              const pid  = p.id || p.Id;
              const fte  = fteMap[pid] || 0;
              const s    = (p.status || p.Status || 'planning').toLowerCase();
              const statusColorMap = {
                active: '#2e7d32', planning: '#1565c0', on_hold: '#e65100',
                completed: '#6a1b9a', cancelled: '#c62828',
              };
              const statusLabelMap = {
                active: 'Aktiv', planning: 'Planifikim', on_hold: 'Pezull',
                completed: 'Përfunduar', cancelled: 'Anuluar',
              };
              const sc = statusColorMap[s] || '#555';
              const sl = statusLabelMap[s] || s;

              return (
                <TableRow
                  key={pid}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openHours(p)}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <FolderOpenIcon sx={{ fontSize: 16, color: '#7986cb' }} />
                      <Typography variant="body2" fontWeight={600}>{p.name || p.Name}</Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="caption" color="text.secondary">
                      {p.client_name || p.ClientName || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={sl} size="small"
                      sx={{ bgcolor: `${sc}18`, color: sc, fontWeight: 700, fontSize: 11 }}
                    />
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center' }}>
                    <Chip
                      label={fte > 0 ? String(fte) : '0'}
                      size="small"
                      sx={{
                        bgcolor: fte > 0 ? '#e8f5e9' : '#f5f5f5',
                        color: fte > 0 ? '#2e7d32' : '#9e9e9e',
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <Button
                        size="small" variant="contained"
                        onClick={(e) => { e.stopPropagation(); openDetail(p); }}
                        sx={{
                          fontSize: 11, py: 0.3, px: 1.25,
                          bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' },
                          textTransform: 'none', borderRadius: 1.5,
                        }}
                      >
                        Fte Plan
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setContextMenu({ anchorEl: e.currentTarget, project: p }); }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Context menu */}
      <Menu
        anchorEl={contextMenu?.anchorEl}
        open={!!contextMenu}
        onClose={() => setContextMenu(null)}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 150 } }}
      >
        <MenuItem
          dense
          onClick={() => { openHours(contextMenu.project); setContextMenu(null); }}
        >
          <Typography variant="body2" fontWeight={600}>Orët</Typography>
        </MenuItem>
        <MenuItem
          dense
          onClick={() => { openDetail(contextMenu.project, 0); setContextMenu(null); }}
        >
          <Typography variant="body2">Orari mujor</Typography>
        </MenuItem>
        <MenuItem dense onClick={() => {
          const p = contextMenu.project;
          setContextMenu(null);
          openWorkRules(p);
        }}>
          <Typography variant="body2">Rregullat e orëve</Typography>
        </MenuItem>
        <MenuItem dense onClick={() => {
          const p = contextMenu.project;
          setContextMenu(null);
          // Open detail then trigger export after data loads (see pendingExport effect)
          openDetail(p, 0);
          setPendingExport(true);
        }}>
          <Typography variant="body2">Export</Typography>
        </MenuItem>      </Menu>

      {/* Per-project work-rules dialog */}
      <Dialog open={!!wrDlg} onClose={() => !wrSaving && setWrDlg(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          Rregullat e orëve
          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontWeight: 400 }}>
            {wrDlg?.name || wrDlg?.Name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {wrLoading || !wrForm ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.25 }}>
              <Grid item xs={6}>
                <TextField label="Orë bruto / ditë" type="number" size="small" fullWidth
                  value={wrForm.daily_gross_minutes != null ? wrForm.daily_gross_minutes / 60 : ''}
                  onChange={e => setWrForm(f => ({ ...f, daily_gross_minutes: Math.round((parseFloat(e.target.value) || 0) * 60) }))}
                  inputProps={{ step: 0.25, min: 0 }} helperText="p.sh. 9 ose 8.5" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Pauzë (min)" type="number" size="small" fullWidth
                  value={wrForm.break_minutes ?? 0}
                  onChange={e => setWrForm(f => ({ ...f, break_minutes: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                  inputProps={{ step: 5, min: 0 }} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={!!wrForm.break_paid}
                    onChange={e => setWrForm(f => ({ ...f, break_paid: e.target.checked }))} />}
                  label={wrForm.break_paid ? 'Pauza paguhet (paid)' : 'Pauza NUK paguhet (unpaid — zbritet)'} />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                  Orë të paguara / ditë: <b>{(wrPaidMinutes / 60).toFixed(2)}h</b>
                  {' '}({wrForm.break_paid ? 'bruto' : 'bruto − pauza unpaid'})
                </Alert>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Ditë / javë" type="number" size="small" fullWidth
                  value={wrForm.work_days_per_week ?? 5}
                  onChange={e => setWrForm(f => ({ ...f, work_days_per_week: Math.min(7, Math.max(1, parseInt(e.target.value, 10) || 5)) }))}
                  inputProps={{ step: 1, min: 1, max: 7 }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Target javor (orë)" type="number" size="small" fullWidth
                  value={wrForm.weekly_target_minutes != null ? wrForm.weekly_target_minutes / 60 : ''}
                  onChange={e => setWrForm(f => ({ ...f, weekly_target_minutes: Math.round((parseFloat(e.target.value) || 0) * 60) }))}
                  inputProps={{ step: 1, min: 0 }} helperText="default 40" />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Mbi target-in javor</InputLabel>
                  <Select label="Mbi target-in javor" value={wrForm.over40_mode || 'cap'}
                    onChange={e => setWrForm(f => ({ ...f, over40_mode: e.target.value }))}>
                    <MenuItem value="cap">Pritet në target (cap)</MenuItem>
                    <MenuItem value="overtime">Shkon overtime</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Shumëzues (opsional)</Typography></Divider></Grid>
              <Grid item xs={4}>
                <TextField label="Weekend" type="number" size="small" fullWidth
                  value={wrForm.weekend_multiplier ?? 1}
                  onChange={e => setWrForm(f => ({ ...f, weekend_multiplier: parseFloat(e.target.value) || 1 }))}
                  inputProps={{ step: 0.05, min: 1 }} />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Holiday" type="number" size="small" fullWidth
                  value={wrForm.holiday_multiplier ?? 1}
                  onChange={e => setWrForm(f => ({ ...f, holiday_multiplier: parseFloat(e.target.value) || 1 }))}
                  inputProps={{ step: 0.05, min: 1 }} />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Night" type="number" size="small" fullWidth
                  value={wrForm.night_multiplier ?? 1}
                  onChange={e => setWrForm(f => ({ ...f, night_multiplier: parseFloat(e.target.value) || 1 }))}
                  inputProps={{ step: 0.05, min: 1 }} />
              </Grid>
              {wrErr && <Grid item xs={12}><Alert severity="error">{wrErr}</Alert></Grid>}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWrDlg(null)} disabled={wrSaving} sx={{ textTransform: 'none' }}>Anulo</Button>
          <Button variant="contained" onClick={saveWorkRules} disabled={wrSaving || wrLoading || !wrForm}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}>
            {wrSaving ? 'Duke ruajtur…' : 'Ruaj'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New project dialog */}
      <Dialog open={newDlg} onClose={() => !creating && setNewDlg(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <CreateNewFolderIcon sx={{ color: 'primary.main' }} />
            Projekt i ri
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          {newErr && <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.82rem' }}>{newErr}</Alert>}
          <Stack gap={1.75} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus fullWidth size="small" label="Emri i projektit *"
              value={newForm.name}
              onChange={e => { setNewForm(f => ({ ...f, name: e.target.value })); setNewErr(''); }}
            />
            <TextField
              fullWidth size="small" label="Përshkrimi" multiline rows={2}
              value={newForm.description}
              onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
              <TextField
                fullWidth size="small" type="date" label="Data e fillimit"
                InputLabelProps={{ shrink: true }}
                value={newForm.start_date}
                onChange={e => setNewForm(f => ({ ...f, start_date: e.target.value }))}
              />
              <TextField
                fullWidth size="small" type="date" label="Data e mbarimit"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: newForm.start_date || undefined }}
                value={newForm.end_date}
                onChange={e => setNewForm(f => ({ ...f, end_date: e.target.value }))}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Statusi</InputLabel>
                <Select
                  label="Statusi" value={newForm.status}
                  onChange={e => setNewForm(f => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="active">Aktiv</MenuItem>
                  <MenuItem value="planning">Planifikim</MenuItem>
                  <MenuItem value="on_hold">Pezull</MenuItem>
                  <MenuItem value="completed">Përfunduar</MenuItem>
                  <MenuItem value="cancelled">Anuluar</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Udhëheqësi (opsional)</InputLabel>
                <Select
                  label="Udhëheqësi (opsional)" value={newForm.lead_id}
                  onChange={e => setNewForm(f => ({ ...f, lead_id: e.target.value }))}
                >
                  <MenuItem value=""><em>— Pa udhëheqës —</em></MenuItem>
                  {workers.map(w => {
                    const wid = String(w.Id || w.id);
                    return (
                      <MenuItem key={wid} value={wid}>
                        {w.FullName || `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim()}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
          <Button onClick={() => setNewDlg(false)} variant="outlined" size="small" disabled={creating}>Anulo</Button>
          <Button
            onClick={handleCreateProject} variant="contained" size="small"
            disabled={creating || !newForm.name.trim()}
            startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <CreateNewFolderIcon />}
          >
            {creating ? 'Duke krijuar...' : 'Krijo projektin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
